<template>
  <div
    class="viz-component"
    ref="component"
    v-resize="onResize"
    :class="[$tvaMq]"
    :style="{ height: height + 'px' }"
  >
    <div
      :class="['background', { isTransitioning: isTransitioning }]"
      :style="{
        top:
          (getVizElSelected && getVizElSelected.data.progress
            ? yScalePos(0)
            : 0) + 'px',
        height: yScaleHeight(hierarchyData.data.weight[vizSwitchSelRes]) + 'px',
        transitionDuration: transitionDuration / 1000 + 's'
      }"
    ></div>
    <svg
      :viewBox="`0 0 ${width} ${height}`"
      :width="width + 'px'"
      :height="height + 'px'"
      :style="{
        transform: `translate3d(${
          $tvaMq === 'desktop' ? 0 : getVizElSelected ? -width : 0
        }px,0,0)`
      }"
    >
      <VizElement
        :elements="hierarchyData.children"
        :elementsWidth="elementsWidth"
        :xScale="xScale"
        :yScalePos="yScalePos"
        :yScaleHeight="yScaleHeight"
        :ancestorsIds="ancestorsIds"
        :isTransitioning="isTransitioning"
        :transitionDuration="transitionDuration"
        :vizSwitchSel="vizSwitchSel"
        @click="setVizSelection($event)"
      />
    </svg>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import genericQueryMethods from "@/mixins/genericQueryMethods";
import VizElement from "@/components/main-viz/VizElement";
import * as d3Scale from "d3-scale";
import resize from "vue-resize-directive";
import _ from "lodash";

export default {
  mixins: [genericQueryMethods],
  data() {
    return {
      width: 0,
      height: 0,
      isTransitioning: false,
      transitionDuration: 800
    };
  },
  watch: {
    getVizElSelected(oldValue, newValue) {
      // if (!oldValue || !newValue) {
      this.startTransitioning();
      // }
    },
    getFilterViz() {
      this.startTransitioning();
    },
    getNormalizeViz() {
      this.startTransitioning();
    }
  },
  computed: {
    vizSwitchSel() {
      return (depth) => {
        // versi, images, normalized
        if (!this.getNormalizeViz) {
          // normalize disabled
          return this.getFilterViz;
        } else {
          if (depth - 1 === this.getNormalizeViz.index) {
            // normalized column
            return "normalized";
          } else if (depth - 1 < this.getNormalizeViz.index) {
            // before "normalized" column
            return `scaled-${this.getNormalizeViz.index}`;
          } else if (depth - 2 === this.getNormalizeViz.index) {
            // first column after "normalized"
            if (this.getFilterViz === "versi") {
              return "percentageVersi";
            } else {
              return "percentageImages";
            }
          } else if (depth - 3 === this.getNormalizeViz.index) {
            // second column after "normalized"
            if (this.getFilterViz === "versi") {
              return "percentageVersiNext";
            } else {
              return "percentageImagesNext";
            }
          } else {
            if (this.getFilterViz === "versi") {
              // third column after "normalized"
              return "percentageVersiNextNext";
            } else {
              return "percentageImagesNextNext";
            }
          }
        }
      };
    },
    vizSwitchSelRes() {
      let res = this.vizSwitchSel(
        this.getVizElSelected ? this.getVizElSelected.depth : 0
      );
      return res;
    },
    levelsAmount() {
      let diff = this.$tvaMq === "desktop" ? 0 : 1;
      return this.getVizElSelected
        ? this.getVizElSelected.height + diff
        : this.hierarchyData.height;
    },
    elementsWidth() {
      if (this.$tvaMq === "desktop") {
        return this.width / this.levelsAmount;
      } else {
        return this.width;
      }
    },
    yMinPos() {
      return this.getVizElSelected
        ? this.getVizElSelected.data.progress[this.vizSwitchSelRes]
        : 0;
    },
    yMaxPos() {
      return this.getVizElSelected
        ? this.getVizElSelected.data.progress[this.vizSwitchSelRes] +
            this.getVizElSelected.data.weight[this.vizSwitchSelRes]
        : this.hierarchyData.data.weight[this.vizSwitchSelRes];
    },
    yMaxHeight() {
      return this.getVizElSelected
        ? this.getVizElSelected.data.weight[this.vizSwitchSelRes]
        : this.hierarchyData.data.weight[this.vizSwitchSelRes];
    },
    yScalePos() {
      return (value) => {
        let scale = d3Scale
          .scaleLinear()
          .domain([this.yMinPos, this.yMaxPos])
          .range([0, this.height]);
        return scale(value);
      };
    },
    yScaleHeight() {
      return (value) => {
        let scale = d3Scale
          .scaleLinear()
          .domain([0, this.yMaxHeight])
          .range([0, this.height]);
        return scale(value);
      };
    },
    xMin() {
      let diff = this.$tvaMq === "desktop" ? 0 : -1;
      return this.getVizElSelected ? this.getVizElSelected.depth + diff : 0;
    },
    xMax() {
      return this.hierarchyData.height - 1;
    },
    xScale() {
      let maxRange =
        this.$tvaMq === "desktop"
          ? this.width - this.elementsWidth
          : this.width * (this.levelsAmount - 1);
      let domainMin = this.xMin === this.xMax ? this.xMin - 1 : this.xMin;
      let rangeMin = this.xMin === this.xMax ? -this.width : 0;
      return (value) => {
        let scale = d3Scale
          .scaleLinear()
          .domain([domainMin, this.xMax])
          .range([rangeMin, maxRange])
          .clamp(false);
        return scale(value);
      };
    },
    ancestorsIds() {
      if (!this.getMainVizTooltip) {
        return [];
      } else {
        return this.getMainVizTooltip.ancestors().map((o) => o.data.id);
      }
    },
    ...mapGetters([
      "getVizElSelected",
      "getFilterViz",
      "getNormalizeViz",
      "getMainVizTooltip",
      "hierarchyData"
    ])
  },
  methods: {
    setDimensions() {
      const component = this.$refs.component;
      this.width =
        this.$tvaMq === "desktop"
          ? component.getBoundingClientRect().width
          : window.innerWidth;
      this.height = window.innerHeight + (this.$tvaMq !== "desktop" ? -40 : 0);
    },
    onResize() {
      this.setDimensions();
    },
    setVizSelection(el) {
      let query = this.$route.query;
      if (query.type && query.type == el.depth && query.id == el.data.id) {
        this.resetQuery();
      } else {
        this.setQuery(el.depth, el.data.id);
      }
    },
    startTransitioning() {
      this.isTransitioning = true;
      setTimeout(() => {
        this.isTransitioning = false;
      }, this.transitionDuration);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setDimensions();
    });
  },
  directives: {
    resize
  },
  components: {
    VizElement
  }
};
</script>
<style lang="scss" scoped>
.viz-component {
  width: 100%;
  position: relative;
  top: 0;
  left: 0;
  .mobile &,
  .tablet & {
    width: 100vw;
    top: 40px;
    position: absolute;
    overflow: hidden;
  }
  .background {
    position: absolute;
    width: 100%;
    left: 0;
    z-index: $z-none;

    background: linear-gradient(
      180deg,
      #96c2c4 -3.84%,
      #bea95e 49.07%,
      #573c46 106.58%
    );
    transition: none;
    &.isTransitioning {
      transition: top 0.8s ease, height 0.8s ease;
    }
  }
  svg {
    position: absolute;
    z-index: $z-a;
    top: 0;
    left: 0;
    border-left: 1px solid $white;
    .mobile &,
    .tablet & {
      overflow: visible;
      transition: transform 0.5s ease;
    }
  }
}
</style>
